import { canonicalShopDomain } from "../api/client";
import type { PurchaseOrderEmailPreview } from "../api/purchase_orders";
import {
  canonicalStockyPublicBigIntId,
  isStockyPublicBigIntId,
  type StockyPublicBigIntId,
} from "../api/stocky_public_ids";
import type { IdempotencyAttempt } from "./idempotency";

// The legacy `ub-purchase-order-email-attempts` key has no tenant owner and is
// never exposed to a shop. Only an exact server-owned reconciliation row may
// bind one legacy record into a verified v2 attempt for the active shop.
const LEGACY_UNSCOPED_STORAGE_KEY = "ub-purchase-order-email-attempts";
const STORAGE_KEY_PREFIX = "ub-purchase-order-email-attempts:v2:";
const STORAGE_ENVELOPE_VERSION = 2;
const CHANGE_EVENT = "ub:purchase-order-email-attempts-changed";

interface PurchaseOrderEmailAttemptEnvelope {
  version: typeof STORAGE_ENVELOPE_VERSION;
  shop_generation: string;
  attempts: PendingPurchaseOrderEmailAttempt[];
}

/**
 * One supplier + the location semantic the merchant requested identify an
 * email-send workflow. `null` intentionally means "use the current primary";
 * it must not be replaced with the resolved location snapshot.
 */
export function purchaseOrderEmailAttemptScope(
  supplierId: StockyPublicBigIntId,
  requestedLocationId: StockyPublicBigIntId | null,
): string {
  return `${canonicalStockyPublicBigIntId(supplierId)}:${
    requestedLocationId === null
      ? "primary"
      : canonicalStockyPublicBigIntId(requestedLocationId)
  }`;
}

/** Keep this byte-for-byte shared by the send screen and reconciliation resume. */
export function purchaseOrderEmailAttemptFingerprint(
  supplierId: StockyPublicBigIntId,
  requestedLocationId: StockyPublicBigIntId | null,
  preview: PurchaseOrderEmailPreview,
  priorDeliveryId: StockyPublicBigIntId | null = null,
  confirmationToken: "send_new_purchase_order" | null = null,
): string {
  const normalizedPreview = normalizePreview(preview);
  return JSON.stringify({
    supplier_id: canonicalStockyPublicBigIntId(supplierId),
    requested_location_id:
      requestedLocationId === null
        ? null
        : canonicalStockyPublicBigIntId(requestedLocationId),
    draft_sha: normalizedPreview.draft_sha,
    from_email: normalizedPreview.from_email,
    to_email: normalizedPreview.to_email,
    subject: normalizedPreview.subject,
    text: normalizedPreview.text,
    attachments: normalizedPreview.attachments,
    include_purchase_order_pdf:
      normalizedPreview.include_purchase_order_pdf,
    prior_delivery_id:
      priorDeliveryId === null
        ? null
        : canonicalStockyPublicBigIntId(priorDeliveryId),
    confirmation_token: confirmationToken,
  });
}

/** Fingerprint emitted by the previous tenant-scoped schema. */
function legacyScopedPurchaseOrderEmailAttemptFingerprint(
  supplierId: StockyPublicBigIntId,
  requestedLocationId: StockyPublicBigIntId | null,
  draftSha: string,
  priorDeliveryId: StockyPublicBigIntId | null = null,
  confirmationToken: "send_new_purchase_order" | null = null,
): string {
  return JSON.stringify({
    supplier_id: canonicalStockyPublicBigIntId(supplierId),
    requested_location_id:
      requestedLocationId === null
        ? null
        : canonicalStockyPublicBigIntId(requestedLocationId),
    draft_sha: draftSha,
    prior_delivery_id:
      priorDeliveryId === null
        ? null
        : canonicalStockyPublicBigIntId(priorDeliveryId),
    confirmation_token: confirmationToken,
  });
}

function legacyPurchaseOrderEmailAttemptFingerprint(
  supplierId: StockyPublicBigIntId,
  requestedLocationId: StockyPublicBigIntId | null,
  draftSha: string,
): string {
  return JSON.stringify({
    supplier_id: canonicalStockyPublicBigIntId(supplierId),
    requested_location_id:
      requestedLocationId === null
        ? null
        : canonicalStockyPublicBigIntId(requestedLocationId),
    draft_sha: draftSha,
  });
}

export interface PendingPurchaseOrderEmailAttempt extends IdempotencyAttempt {
  /** Immutable verified tenant generation that owns this provider operation. */
  shopGeneration: string;
  supplierId: StockyPublicBigIntId;
  requestedLocationId: StockyPublicBigIntId | null;
  preview: PurchaseOrderEmailPreview;
  /** UNKNOWN/unresolved outcomes are review-only and must never be retried. */
  disposition: "retryable" | "reconciliation_required";
  deliveryId: StockyPublicBigIntId | null;
  priorDeliveryId: StockyPublicBigIntId | null;
  confirmationToken: "send_new_purchase_order" | null;
}

export class PurchaseOrderEmailAttemptStorageError extends Error {}
export class PurchaseOrderEmailAttemptConflictError extends Error {}

function requireShopGeneration(shopGeneration: string): string {
  const separator = shopGeneration.indexOf(":");
  const shopIdText = shopGeneration.slice(0, separator);
  const shopDomainText = shopGeneration.slice(separator + 1);
  const shopDomain = canonicalShopDomain(shopDomainText);
  if (
    separator < 1 ||
    !isStockyPublicBigIntId(shopIdText) ||
    shopDomain === null ||
    `${shopIdText}:${shopDomain}` !== shopGeneration
  ) {
    throw new PurchaseOrderEmailAttemptStorageError(
      "verified shop generation is unavailable",
    );
  }
  return shopGeneration;
}

function storageKey(shopGeneration: string): string {
  return `${STORAGE_KEY_PREFIX}${encodeURIComponent(
    requireShopGeneration(shopGeneration),
  )}`;
}

function isPreview(value: unknown): value is PurchaseOrderEmailPreview {
  if (value === null || typeof value !== "object") return false;
  const preview = value as Partial<PurchaseOrderEmailPreview>;
  return (
    typeof preview.draft_sha === "string" &&
    isStockyPublicBigIntId(preview.supplier_id) &&
    (preview.location_id === null ||
      isStockyPublicBigIntId(preview.location_id)) &&
    typeof preview.input_epoch === "number" &&
    typeof preview.from_email === "string" &&
    typeof preview.to_email === "string" &&
    typeof preview.subject === "string" &&
    typeof preview.text === "string" &&
    (preview.attachments === undefined ||
      (Array.isArray(preview.attachments) &&
        preview.attachments.every(
          (attachment) =>
            attachment !== null &&
            typeof attachment === "object" &&
            typeof attachment.filename === "string" &&
            typeof attachment.content_type === "string" &&
            typeof attachment.content === "string",
        ))) &&
    (preview.include_purchase_order_pdf === undefined ||
      typeof preview.include_purchase_order_pdf === "boolean") &&
    (preview.purchase_order_note === undefined ||
      preview.purchase_order_note === null ||
      typeof preview.purchase_order_note === "string")
  );
}

function normalizePreview(
  preview: PurchaseOrderEmailPreview,
): PurchaseOrderEmailPreview {
  return {
    ...preview,
    supplier_id: canonicalStockyPublicBigIntId(preview.supplier_id),
    location_id:
      preview.location_id === null
        ? null
        : canonicalStockyPublicBigIntId(preview.location_id),
    purchase_order_note: preview.purchase_order_note ?? null,
    attachments: (preview.attachments ?? []).map((attachment) => ({
      ...attachment,
    })),
    include_purchase_order_pdf:
      preview.include_purchase_order_pdf !== false,
  };
}

function isPendingAttempt(
  value: unknown,
  shopGeneration: string,
): value is PendingPurchaseOrderEmailAttempt {
  if (value === null || typeof value !== "object") return false;
  const attempt = value as Partial<PendingPurchaseOrderEmailAttempt>;
  if (
    attempt.shopGeneration !== shopGeneration ||
    typeof attempt.key !== "string" ||
    typeof attempt.fingerprint !== "string" ||
    !isStockyPublicBigIntId(attempt.supplierId) ||
    !(
      attempt.requestedLocationId === null ||
      isStockyPublicBigIntId(attempt.requestedLocationId)
    ) ||
    !isPreview(attempt.preview)
  ) {
    return false;
  }
  if (
    attempt.disposition !== undefined &&
    attempt.disposition !== "retryable" &&
    attempt.disposition !== "reconciliation_required"
  ) {
    return false;
  }
  if (
    attempt.deliveryId !== undefined &&
    attempt.deliveryId !== null &&
    !isStockyPublicBigIntId(attempt.deliveryId)
  ) {
    return false;
  }
  if (
    attempt.priorDeliveryId !== undefined &&
    attempt.priorDeliveryId !== null &&
    !isStockyPublicBigIntId(attempt.priorDeliveryId)
  ) {
    return false;
  }
  if (
    attempt.confirmationToken !== undefined &&
    attempt.confirmationToken !== null &&
    attempt.confirmationToken !== "send_new_purchase_order"
  ) {
    return false;
  }
  const expected = purchaseOrderEmailAttemptFingerprint(
    attempt.supplierId,
    attempt.requestedLocationId,
    attempt.preview,
    attempt.priorDeliveryId ?? null,
    attempt.confirmationToken ?? null,
  );
  const legacyScoped = legacyScopedPurchaseOrderEmailAttemptFingerprint(
    attempt.supplierId,
    attempt.requestedLocationId,
    attempt.preview.draft_sha,
    attempt.priorDeliveryId ?? null,
    attempt.confirmationToken ?? null,
  );
  return (
    canonicalStockyPublicBigIntId(attempt.preview.supplier_id) ===
      canonicalStockyPublicBigIntId(attempt.supplierId) &&
    (attempt.fingerprint === expected || attempt.fingerprint === legacyScoped)
  );
}

export function listPurchaseOrderEmailAttempts(
  shopGeneration: string,
): PendingPurchaseOrderEmailAttempt[] {
  if (typeof window === "undefined") return [];
  const normalizedGeneration = requireShopGeneration(shopGeneration);
  try {
    const raw = window.sessionStorage.getItem(storageKey(normalizedGeneration));
    if (raw === null) return [];
    const parsed: unknown = JSON.parse(raw);
    if (
      parsed === null ||
      typeof parsed !== "object" ||
      Array.isArray(parsed) ||
      (parsed as Partial<PurchaseOrderEmailAttemptEnvelope>).version !==
        STORAGE_ENVELOPE_VERSION ||
      (parsed as Partial<PurchaseOrderEmailAttemptEnvelope>).shop_generation !==
        normalizedGeneration ||
      !Array.isArray(
        (parsed as Partial<PurchaseOrderEmailAttemptEnvelope>).attempts,
      )
    ) {
      throw new PurchaseOrderEmailAttemptStorageError(
        "pending email attempt envelope is invalid",
      );
    }
    const attempts = (parsed as PurchaseOrderEmailAttemptEnvelope).attempts;
    const normalized = attempts
      .filter((attempt) => isPendingAttempt(attempt, normalizedGeneration))
      .map((attempt) => ({
        ...attempt,
        supplierId: canonicalStockyPublicBigIntId(attempt.supplierId),
        requestedLocationId:
          attempt.requestedLocationId === null
            ? null
            : canonicalStockyPublicBigIntId(attempt.requestedLocationId),
        preview: normalizePreview(attempt.preview),
        disposition: attempt.disposition ?? "retryable",
        deliveryId:
          attempt.deliveryId == null
            ? null
            : canonicalStockyPublicBigIntId(attempt.deliveryId),
        priorDeliveryId:
          attempt.priorDeliveryId == null
            ? null
            : canonicalStockyPublicBigIntId(attempt.priorDeliveryId),
        confirmationToken: attempt.confirmationToken ?? null,
        fingerprint: purchaseOrderEmailAttemptFingerprint(
          attempt.supplierId,
          attempt.requestedLocationId,
          attempt.preview,
          attempt.priorDeliveryId ?? null,
          attempt.confirmationToken ?? null,
        ),
      }));
    if (normalized.length !== attempts.length) {
      throw new PurchaseOrderEmailAttemptStorageError(
        "pending email attempt record is invalid",
      );
    }
    return normalized;
  } catch (error) {
    if (error instanceof PurchaseOrderEmailAttemptStorageError) throw error;
    throw new PurchaseOrderEmailAttemptStorageError(
      error instanceof Error ? error.message : "session storage read failed",
    );
  }
}

export function readPurchaseOrderEmailAttempt(
  shopGeneration: string,
  supplierId: StockyPublicBigIntId,
  requestedLocationId: StockyPublicBigIntId | null,
): PendingPurchaseOrderEmailAttempt | null {
  return (
    listPurchaseOrderEmailAttempts(shopGeneration).find(
      (attempt) =>
        attempt.supplierId === canonicalStockyPublicBigIntId(supplierId) &&
        attempt.requestedLocationId ===
          (requestedLocationId === null
            ? null
            : canonicalStockyPublicBigIntId(requestedLocationId)),
    ) ?? null
  );
}

/** Find an unresolved local attempt even if the screen's location has changed. */
export function findPurchaseOrderEmailAttemptForSupplier(
  shopGeneration: string,
  supplierId: StockyPublicBigIntId,
): PendingPurchaseOrderEmailAttempt | null {
  return (
    listPurchaseOrderEmailAttempts(shopGeneration).find(
      (attempt) =>
        attempt.supplierId === canonicalStockyPublicBigIntId(supplierId),
    ) ?? null
  );
}

function sameAttempt(
  left: PendingPurchaseOrderEmailAttempt,
  right: PendingPurchaseOrderEmailAttempt,
): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

/**
 * Persistence is part of the safety contract, not a best-effort convenience.
 * A provider POST is forbidden unless write + readback of the exact record
 * succeeds; otherwise a response loss followed by reload could mint a new key.
 */
function writeAndVerify(
  shopGeneration: string,
  attempts: PendingPurchaseOrderEmailAttempt[],
  expected: PendingPurchaseOrderEmailAttempt,
): void {
  if (typeof window === "undefined") {
    throw new PurchaseOrderEmailAttemptStorageError(
      "session storage is unavailable",
    );
  }
  const normalizedGeneration = requireShopGeneration(shopGeneration);
  if (expected.shopGeneration !== normalizedGeneration) {
    throw new PurchaseOrderEmailAttemptStorageError(
      "pending email attempt belongs to another shop",
    );
  }
  try {
    const envelope: PurchaseOrderEmailAttemptEnvelope = {
      version: STORAGE_ENVELOPE_VERSION,
      shop_generation: normalizedGeneration,
      attempts,
    };
    window.sessionStorage.setItem(
      storageKey(normalizedGeneration),
      JSON.stringify(envelope),
    );
    const readback = listPurchaseOrderEmailAttempts(normalizedGeneration).find(
      (attempt) =>
        attempt.supplierId === expected.supplierId &&
        attempt.requestedLocationId === expected.requestedLocationId,
    );
    if (!readback || !sameAttempt(readback, expected)) {
      throw new Error("session storage readback mismatch");
    }
  } catch (error) {
    throw new PurchaseOrderEmailAttemptStorageError(
      error instanceof Error ? error.message : "session storage write failed",
    );
  }
  window.dispatchEvent(
    new CustomEvent(CHANGE_EVENT, {
      detail: { shopGeneration: normalizedGeneration },
    }),
  );
}

interface PersistPurchaseOrderEmailAttemptInput {
  shopGeneration: string;
  supplierId: StockyPublicBigIntId;
  requestedLocationId: StockyPublicBigIntId | null;
  preview: PurchaseOrderEmailPreview;
  previous?: PendingPurchaseOrderEmailAttempt | null;
  idempotencyKey?: string;
  priorDeliveryId?: StockyPublicBigIntId | null;
  confirmationToken?: "send_new_purchase_order" | null;
}

function legacyQuarantinePresent(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.sessionStorage.getItem(LEGACY_UNSCOPED_STORAGE_KEY) !== null;
  } catch {
    return true;
  }
}

function persistPurchaseOrderEmailAttemptInternal(
  input: PersistPurchaseOrderEmailAttemptInput,
  serverOwnershipVerified: boolean,
): PendingPurchaseOrderEmailAttempt {
  const shopGeneration = requireShopGeneration(input.shopGeneration);
  const supplierId = canonicalStockyPublicBigIntId(input.supplierId);
  const requestedLocationId =
    input.requestedLocationId === null
      ? null
      : canonicalStockyPublicBigIntId(input.requestedLocationId);
  const preview = normalizePreview(input.preview);
  if (preview.supplier_id !== supplierId) {
    throw new PurchaseOrderEmailAttemptConflictError(
      "the email preview belongs to another supplier",
    );
  }
  const storedAttempts = listPurchaseOrderEmailAttempts(shopGeneration);
  const supplierAttempt = storedAttempts.find(
    (attempt) => attempt.supplierId === supplierId,
  );
  if (
    supplierAttempt === undefined &&
    legacyQuarantinePresent() &&
    !serverOwnershipVerified
  ) {
    throw new PurchaseOrderEmailAttemptConflictError(
      "an unowned legacy email attempt requires server reconciliation",
    );
  }
  if (
    input.previous !== undefined &&
    input.previous !== null &&
    input.previous.shopGeneration !== shopGeneration
  ) {
    throw new PurchaseOrderEmailAttemptConflictError(
      "the previous email attempt belongs to another shop",
    );
  }
  const selectedPriorDeliveryId =
    input.priorDeliveryId ??
    input.previous?.priorDeliveryId ??
    supplierAttempt?.priorDeliveryId ??
    null;
  const priorDeliveryId =
    selectedPriorDeliveryId === null
      ? null
      : canonicalStockyPublicBigIntId(selectedPriorDeliveryId);
  const confirmationToken =
    input.confirmationToken ??
    input.previous?.confirmationToken ??
    supplierAttempt?.confirmationToken ??
    null;
  const fingerprint = purchaseOrderEmailAttemptFingerprint(
    supplierId,
    requestedLocationId,
    preview,
    priorDeliveryId,
    confirmationToken,
  );
  const matching = [supplierAttempt ?? null, input.previous ?? null].find(
    (candidate) => candidate?.fingerprint === fingerprint,
  );
  if (supplierAttempt && !matching) {
    throw new PurchaseOrderEmailAttemptConflictError(
      "another unresolved attempt exists for this supplier",
    );
  }
  if (
    supplierAttempt &&
    input.idempotencyKey !== undefined &&
    input.idempotencyKey !== supplierAttempt.key
  ) {
    throw new PurchaseOrderEmailAttemptConflictError(
      "a different operation key is already pending for this supplier",
    );
  }
  const attempt: PendingPurchaseOrderEmailAttempt = {
    shopGeneration,
    supplierId,
    requestedLocationId,
    preview: matching?.preview ?? preview,
    fingerprint,
    key: matching?.key ?? input.idempotencyKey ?? crypto.randomUUID(),
    disposition: matching?.disposition ?? "retryable",
    deliveryId: matching?.deliveryId ?? null,
    priorDeliveryId,
    confirmationToken,
  };
  const remaining = storedAttempts.filter(
    (candidate) => candidate.supplierId !== supplierId,
  );
  writeAndVerify(shopGeneration, [...remaining, attempt], attempt);
  return attempt;
}

/** Persist exact payload + hash + key atomically before the first POST. */
export function persistPurchaseOrderEmailAttempt(
  input: PersistPurchaseOrderEmailAttemptInput,
): PendingPurchaseOrderEmailAttempt {
  return persistPurchaseOrderEmailAttemptInternal(input, false);
}

export function clearPurchaseOrderEmailAttempt(
  attempt: PendingPurchaseOrderEmailAttempt,
): void {
  if (typeof window === "undefined") return;
  let shopGeneration: string;
  try {
    shopGeneration = requireShopGeneration(attempt.shopGeneration);
  } catch {
    return;
  }
  const current = findPurchaseOrderEmailAttemptForSupplier(
    shopGeneration,
    attempt.supplierId,
  );
  if (
    current?.key !== attempt.key ||
    current.fingerprint !== attempt.fingerprint
  ) {
    return;
  }
  try {
    const remaining = listPurchaseOrderEmailAttempts(shopGeneration).filter(
      (candidate) => candidate.supplierId !== attempt.supplierId,
    );
    if (remaining.length === 0) {
      window.sessionStorage.removeItem(storageKey(shopGeneration));
    } else {
      const envelope: PurchaseOrderEmailAttemptEnvelope = {
        version: STORAGE_ENVELOPE_VERSION,
        shop_generation: shopGeneration,
        attempts: remaining,
      };
      window.sessionStorage.setItem(
        storageKey(shopGeneration),
        JSON.stringify(envelope),
      );
    }
    window.dispatchEvent(
      new CustomEvent(CHANGE_EVENT, { detail: { shopGeneration } }),
    );
  } catch {
    // A stale same-key replay is safe; never clear a different record.
  }
}

function removeServerVerifiedLegacyAttempt(
  input: PersistPurchaseOrderEmailAttemptInput & { idempotencyKey: string },
): void {
  if (typeof window === "undefined") return;
  try {
    const raw = window.sessionStorage.getItem(LEGACY_UNSCOPED_STORAGE_KEY);
    if (raw === null) return;
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return;
    const supplierId = canonicalStockyPublicBigIntId(input.supplierId);
    const requestedLocationId =
      input.requestedLocationId === null
        ? null
        : canonicalStockyPublicBigIntId(input.requestedLocationId);
    const normalizedPreview = normalizePreview(input.preview);
    const priorDeliveryId =
      input.priorDeliveryId == null
        ? null
        : canonicalStockyPublicBigIntId(input.priorDeliveryId);
    const confirmationToken = input.confirmationToken ?? null;
    const expectedFingerprint = purchaseOrderEmailAttemptFingerprint(
      supplierId,
      requestedLocationId,
      normalizedPreview,
      priorDeliveryId,
      confirmationToken,
    );
    const legacyFingerprint = legacyPurchaseOrderEmailAttemptFingerprint(
      supplierId,
      requestedLocationId,
      normalizedPreview.draft_sha,
    );
    const matches = (candidate: unknown): boolean => {
      if (candidate === null || typeof candidate !== "object") return false;
      const attempt = candidate as Record<string, unknown>;
      const preview = attempt.preview;
      const fingerprint = attempt.fingerprint;
      const candidateSupplierId = isStockyPublicBigIntId(attempt.supplierId)
        ? canonicalStockyPublicBigIntId(attempt.supplierId)
        : null;
      const candidateRequestedLocationId =
        attempt.requestedLocationId === null
          ? null
          : isStockyPublicBigIntId(attempt.requestedLocationId)
            ? canonicalStockyPublicBigIntId(attempt.requestedLocationId)
            : undefined;
      return (
        attempt.key === input.idempotencyKey &&
        candidateSupplierId === supplierId &&
        candidateRequestedLocationId === requestedLocationId &&
        isPreview(preview) &&
        JSON.stringify(normalizePreview(preview)) ===
          JSON.stringify(normalizedPreview) &&
        (fingerprint === expectedFingerprint ||
          (priorDeliveryId === null &&
            confirmationToken === null &&
            fingerprint === legacyFingerprint))
      );
    };
    const remaining = parsed.filter((candidate) => !matches(candidate));
    if (remaining.length === parsed.length) return;
    if (remaining.length === 0) {
      window.sessionStorage.removeItem(LEGACY_UNSCOPED_STORAGE_KEY);
    } else {
      window.sessionStorage.setItem(
        LEGACY_UNSCOPED_STORAGE_KEY,
        JSON.stringify(remaining),
      );
    }
  } catch {
    // The verified v2 record remains safe. Keeping an unreadable quarantine
    // deliberately blocks unrelated new sends until support can reconcile it.
  }
}

/** Restore the exact durable reconciliation row before returning to send. */
export function rememberPurchaseOrderEmailAttempt(input: {
  shopGeneration: string;
  supplierId: StockyPublicBigIntId;
  requestedLocationId: StockyPublicBigIntId | null;
  preview: PurchaseOrderEmailPreview;
  idempotencyKey: string;
  priorDeliveryId?: StockyPublicBigIntId | null;
  confirmationToken?: "send_new_purchase_order" | null;
}): PendingPurchaseOrderEmailAttempt {
  const attempt = persistPurchaseOrderEmailAttemptInternal(
    {
      ...input,
      idempotencyKey: input.idempotencyKey,
    },
    true,
  );
  removeServerVerifiedLegacyAttempt(input);
  return attempt;
}

/** Freeze the explicit recent-delivery override before any override POST. */
export function addPurchaseOrderEmailDeliveryOverride(
  attempt: PendingPurchaseOrderEmailAttempt,
  priorDeliveryId: StockyPublicBigIntId,
  confirmationToken: "send_new_purchase_order",
): PendingPurchaseOrderEmailAttempt {
  const canonicalPriorDeliveryId =
    canonicalStockyPublicBigIntId(priorDeliveryId);
  const stored = findPurchaseOrderEmailAttemptForSupplier(
    attempt.shopGeneration,
    attempt.supplierId,
  );
  if (
    stored === null ||
    stored.key !== attempt.key ||
    stored.preview.draft_sha !== attempt.preview.draft_sha
  ) {
    throw new PurchaseOrderEmailAttemptConflictError(
      "the pending email attempt changed before the override was recorded",
    );
  }
  const overridden: PendingPurchaseOrderEmailAttempt = {
    ...stored,
    priorDeliveryId: canonicalPriorDeliveryId,
    confirmationToken,
    fingerprint: purchaseOrderEmailAttemptFingerprint(
      stored.supplierId,
      stored.requestedLocationId,
      stored.preview,
      canonicalPriorDeliveryId,
      confirmationToken,
    ),
  };
  const remaining = listPurchaseOrderEmailAttempts(
    attempt.shopGeneration,
  ).filter((candidate) => candidate.supplierId !== attempt.supplierId);
  writeAndVerify(
    attempt.shopGeneration,
    [...remaining, overridden],
    overridden,
  );
  return overridden;
}

/** Persist the server's instruction that this outcome is review-only. */
export function requirePurchaseOrderEmailReconciliation(
  attempt: PendingPurchaseOrderEmailAttempt,
  deliveryId: StockyPublicBigIntId | null,
): PendingPurchaseOrderEmailAttempt {
  const canonicalDeliveryId =
    deliveryId === null ? null : canonicalStockyPublicBigIntId(deliveryId);
  const stored = findPurchaseOrderEmailAttemptForSupplier(
    attempt.shopGeneration,
    attempt.supplierId,
  );
  if (
    stored === null ||
    stored.key !== attempt.key ||
    stored.fingerprint !== attempt.fingerprint
  ) {
    throw new PurchaseOrderEmailAttemptConflictError(
      "the pending email attempt changed before reconciliation was recorded",
    );
  }
  const blocked: PendingPurchaseOrderEmailAttempt = {
    ...stored,
    disposition: "reconciliation_required",
    deliveryId: canonicalDeliveryId,
  };
  const remaining = listPurchaseOrderEmailAttempts(
    attempt.shopGeneration,
  ).filter((candidate) => candidate.supplierId !== attempt.supplierId);
  writeAndVerify(attempt.shopGeneration, [...remaining, blocked], blocked);
  return blocked;
}

export function subscribePurchaseOrderEmailAttempts(
  shopGeneration: string,
  listener: () => void,
): () => void {
  if (typeof window === "undefined") return () => undefined;
  const normalizedGeneration = requireShopGeneration(shopGeneration);
  const onChange = (event: Event) => {
    if (
      event instanceof CustomEvent &&
      (event.detail as { shopGeneration?: unknown } | null)?.shopGeneration ===
        normalizedGeneration
    ) {
      listener();
    }
  };
  window.addEventListener(CHANGE_EVENT, onChange);
  return () => window.removeEventListener(CHANGE_EVENT, onChange);
}
